import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@shared/components/Table';
import { majorContractsService } from '@shared/services/majorContracts';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

const CONTRACT_TYPE_TO_DOC_TYPE = {
    laborContract: 'HR_CONTRACT',
    additionalLaborContract: 'HR_CONTRACT',
    additionalAgreement: 'HR_ANEX',
    employerChange: 'HR_ANEX',
    termination: 'HR_LEAVE',
};

const getFilenameFromDisposition = (contentDisposition, fallbackName = 'report.docx') => {
    if (!contentDisposition) return fallbackName;
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
    const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (plainMatch?.[1]) return plainMatch[1];
    return fallbackName;
};

export default function ContractWordView({ setOverlayView, contractType, contractId }) {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const mappedTypeDoc = CONTRACT_TYPE_TO_DOC_TYPE[contractType];

    const columns = [
        {
            label: t('users.table.headers.firstName') || 'Name',
            key: 'name',
        },
    ];

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await majorContractsService.getHrDocTemplates({
                    typeDoc: mappedTypeDoc,
                });

                // API may return paged content, array, or single object.
                if (Array.isArray(response)) {
                    setData(response);
                } else if (Array.isArray(response?.content)) {
                    setData(response.content);
                } else if (response && typeof response === 'object') {
                    setData([response]);
                } else {
                    setData([]);
                }
            } catch (err) {
                setError(err?.response?.data?.message || err?.message || 'Failed to fetch data');
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        loadTemplates();
    }, [mappedTypeDoc]);

    const handleRowClick = async (index) => {
        const selectedRow = data?.[index];
        const reportId = selectedRow?.reportId ?? selectedRow?.id;

        if (!contractId) {
            notifyError(t('persons.personContract.selectContractFirst') || 'Please select a contract first.');
            return;
        }

        if (!reportId) {
            notifyError(t('common.errorMessage') || 'Missing report id.');
            return;
        }

        try {
            const response = await majorContractsService.generateContractReport(reportId, contractId);
            const blob = response?.data;
            const contentDisposition = response?.headers?.['content-disposition'];
            const fileName = getFilenameFromDisposition(contentDisposition, `report-${reportId}.docx`);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            notifyError(err?.response?.data?.message || err?.message || t('common.errorMessage'));
        }
    };

    return (
        <div className="flex-column">
            {/* <h3 className="form-subtitle">{t('persons.personContract.documents') || 'Documents'}</h3> */}
            <Table
                title={t('persons.personContract.documents') || 'Documents'}
                columns={columns}
                data={data}
                loading={loading}
                error={error}
                pagination={false}
                className="width-100"
                showPagination={false}
                showPageSizeSelector={false}
                showActiveSelect={false}
                showCompanySelect={false}
                showLevelSelect={false}
                showPreviousPage={false}
                showCustomSearch={true}
                handleRowClick={handleRowClick}
            />
            <div className="form-controls">
                <button
                    type="button"
                    className="form-btn btn-red"
                    onClick={() => setOverlayView(null)}
                    title={t('common.exit') || 'Exit'}
                >
                    {t('common.exit') || 'Exit'}
                </button>
            </div>
        </div>
    );
}
